/**
 * Helpers for normalizing, formatting and comparing keyboard shortcuts
 * used by the Control Room (régie) event nodes.
 *
 * A shortcut is represented as 1–3 key codes stored on the event node
 * (`shortcutKey1`, `shortcutKey2?`, `shortcutKey3?`).  The canonical form
 * used for matching at runtime and in the WS protocol is a **normalized
 * combo string** - a `+`-joined, sorted, lowercased sequence of key codes.
 *
 * @example
 * normalizeShortcut("Ctrl", "Shift", "T") // "ctrl+shift+t"
 * normalizeShortcut("T")                  // "t"
 */

const MODIFIER_ORDER: Record<string, number> = {
  ctrl: 0,
  control: 0,
  meta: 1,
  alt: 2,
  shift: 3,
};

function keyOrder(key: string): number {
  return MODIFIER_ORDER[key.toLowerCase()] ?? 100;
}

/**
 * Normalizes 1–3 key names into a canonical combo string.
 *
 * Rules:
 * - All keys are lowercased and trimmed.
 * - Duplicates are removed.
 * - Modifiers come first in a stable order (ctrl < meta < alt < shift),
 *   then non-modifier keys in lexicographic order.
 *
 * @param key1 - First (required) key.
 * @param key2 - Optional second key.
 * @param key3 - Optional third key.
 * @returns A canonical combo string (e.g. `"ctrl+shift+t"`), or `""` if
 *   `key1` is falsy.
 */
export function normalizeShortcut(
  key1: string,
  key2?: string,
  key3?: string,
): string {
  const keys = [key1, key2, key3]
    .filter((k): k is string => Boolean(k?.trim()))
    .map((k) => {
      const key = k.trim().toLowerCase();
      return key === "control" ? "ctrl" : key;
    });

  const unique = [...new Set(keys)];
  unique.sort((a, b) => {
    const oa = keyOrder(a);
    const ob = keyOrder(b);
    if (oa !== ob) return oa - ob;
    return a.localeCompare(b);
  });

  return unique.join("+");
}

export type ShortcutPreference = {
  shortcut: string;
  key1: string;
  key2?: string;
  key3?: string;
};

/** A missing preference uses the admin's default; an empty preference disables it. */
export function resolveShortcutCombo(
  node: {
    shortcutBinding?: string;
    shortcutKey1?: string;
    shortcutKey2?: string;
    shortcutKey3?: string;
  },
  preferences: readonly ShortcutPreference[] = [],
): string {
  const preference = node.shortcutBinding
    ? preferences.find((item) => item.shortcut === node.shortcutBinding)
    : undefined;
  return preference
    ? normalizeShortcut(preference.key1, preference.key2, preference.key3)
    : normalizeShortcut(
        node.shortcutKey1 ?? "",
        node.shortcutKey2,
        node.shortcutKey3,
      );
}

/**
 * Formats a normalized combo string for display.
 *
 * @example
 * formatShortcutDisplay("ctrl+shift+t") // "Ctrl + Shift + T"
 */
export function formatShortcutDisplay(combo: string): string {
  if (!combo) return "";
  return combo
    .split("+")
    .map((k) => {
      if (k === "meta") return "⌘";
      if (k === "control" || k === "ctrl") return "Ctrl";
      if (k === "alt") return "Alt";
      if (k === "shift") return "Shift";
      if (k === " " || k === "space") return "Space";
      if (k === "arrowup") return "↑";
      if (k === "arrowdown") return "↓";
      if (k === "arrowleft") return "←";
      if (k === "arrowright") return "→";
      if (k === "escape") return "Esc";
      if (k === "enter") return "Enter";
      if (k === "backspace") return "⌫";
      if (k === "delete") return "Del";
      if (k === "tab") return "Tab";
      if (k.length === 1) return k.toUpperCase();
      return k.charAt(0).toUpperCase() + k.slice(1);
    })
    .join(" + ");
}

/**
 * Converts a browser `KeyboardEvent` into the normalized key code used
 * by the shortcut system.
 *
 * This favours `event.key` over `event.code` for layout-independent naming,
 * except for modifier keys where the raw key name is used directly.
 */
export function keyboardEventToKey(event: KeyboardEvent): string {
  const key = event.key;
  if (key === "Control") return "ctrl";
  if (key === "Meta") return "meta";
  if (key === "Alt") return "alt";
  if (key === "Shift") return "shift";
  return key.toLowerCase();
}

/**
 * Builds a normalized combo from a live `KeyboardEvent`, capturing the
 * pressed key together with any active modifier keys.
 */
export function keyboardEventToCombo(
  event: KeyboardEvent,
  pressedKeys: readonly string[] = [],
): string {
  const parts: string[] = [];
  if (event.ctrlKey) parts.push("ctrl");
  if (event.metaKey) parts.push("meta");
  if (event.altKey) parts.push("alt");
  if (event.shiftKey) parts.push("shift");

  const main = keyboardEventToKey(event);
  for (const key of pressedKeys) {
    if (
      !["ctrl", "control", "meta", "alt", "shift"].includes(key) &&
      !parts.includes(key)
    )
      parts.push(key);
  }
  if (!["ctrl", "meta", "alt", "shift"].includes(main)) {
    if (!parts.includes(main)) parts.push(main);
  }

  return parts.length > 3
    ? ""
    : normalizeShortcut(parts[0] ?? "", parts[1], parts[2]);
}

/** Capture modifier flags too: some platforms emit only the final key event. */
export function captureShortcutKeys(
  event: KeyboardEvent,
  previous: readonly string[] = [],
): string[] {
  const combo = keyboardEventToCombo(event);
  if (!combo) return [...previous];
  const keys = [...new Set([...previous, ...combo.split("+")])];
  return keys.length <= 3
    ? normalizeShortcut(keys[0], keys[1], keys[2]).split("+")
    : [...previous];
}

/**
 * Validates that a node's shortcut fields form a publishable event.
 *
 * A control-room event is publishable when `signalName`, `signalDescription`
 * and at least `shortcutKey1` are non-empty.
 */
export function isControlRoomEventPublishable(node: {
  signalName?: string;
  signalDescription?: string;
  shortcutKey1?: string;
}): boolean {
  return Boolean(
    node.signalName?.trim() &&
      node.signalDescription?.trim() &&
      node.shortcutKey1?.trim(),
  );
}
